import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, User, Briefcase, MapPin, Calendar, Building2, Award, Monitor } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export const ProfileSection = ({ profile }) => {
  const ProfileField = ({ label, value, icon: Icon }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-medium text-sm">{value || "—"}</span>
        <div className="profile-lock">
          <Lock className="w-3 h-3" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        The information below has been pre-populated from your employee records. 
        If any information is incorrect, please contact HR.
      </p>

      {/* Personal Information */}
      <Card className="assessment-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#003366] flex items-center gap-2">
            <User className="w-5 h-5" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ProfileField label="Full Name" value={profile?.fullName} />
          <ProfileField label="Email" value={profile?.email} />
          <ProfileField label="Job Title" value={profile?.jobTitle} icon={Briefcase} />
          <ProfileField label="Job Grade" value={profile?.jobGrade} />
          <ProfileField label="Location" value={profile?.location} icon={MapPin} />
          <ProfileField label="Operating Entities" value={profile?.operatingEntities} icon={Building2} />
        </CardContent>
      </Card>

      {/* Employment Information */}
      <Card className="assessment-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#003366] flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Employment Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ProfileField label="Hire Date" value={profile?.hireDate} />
          <ProfileField label="Years at Sagicor" value={profile?.yearsAtSagicor} />
          <ProfileField label="Current Role Since" value={profile?.currentRoleSince} />
          <ProfileField label="Department" value={profile?.department} />
          <ProfileField label="Business Unit" value={profile?.businessUnit} />
        </CardContent>
      </Card>

      {/* Reporting Structure */}
      <Card className="assessment-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#003366] flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Reporting Structure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ProfileField label="Direct Manager" value={profile?.managerName} />
          <ProfileField label="Manager Title" value={profile?.managerTitle} />
          <ProfileField label="Department Head" value={profile?.departmentHead} />
        </CardContent>
      </Card>

      {/* Performance Snapshot */}
      <Card className="assessment-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#003366] flex items-center gap-2">
            <Award className="w-5 h-5" />
            Performance Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ProfileField label="2023 Performance" value={profile?.performanceRating} />
          <ProfileField label="Performance Note" value={profile?.performanceNote} />
          <ProfileField label="Recognition" value={profile?.recognition} />
        </CardContent>
      </Card>

      {/* Technology Environment */}
      {profile?.technologies && profile.technologies.length > 0 && (
        <Card className="assessment-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#003366] flex items-center gap-2">
              <Monitor className="w-5 h-5" />
              Technology Environment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Technologies associated with your role:
            </p>
            <div className="flex flex-wrap gap-2">
              {profile.technologies.map((tech, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="px-3 py-1 border-[#003366]/20"
                >
                  {tech.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfileSection;
